import React from 'react';
import {View} from 'react-native';
import Ionicons from 'react-native-vector-icons/Ionicons';

import CCText from '../../../Atoms/CCText';
import {S, STATUS, T} from '../courseTheme';
import {E, HEADER_HEIGHT} from './editorTheme';
import type {FailingCheck} from './editorLogic';
import {Button, IconButton, useEscape} from './ui';

type Props = {
  title: string;
  reviewState: string;
  progress: {done: number; total: number};
  next: {id: number; number: number; why: 'flagged' | 'unopened'} | null;
  onNext: () => void;
  onBack: () => void;
  /** Open the course's rename sheet. */
  onRename: () => void;
  /** Shown when the rail is hidden (wide) or always (narrow, as the drawer toggle). */
  showRailToggle: boolean;
  onShowRail: () => void;
  narrow: boolean;
  gateOk: boolean;
  failing: FailingCheck[];
  publishing: boolean;
  onPublish: () => void;
  onUnpublish: () => void;
  /** Publish although some checks have not passed: they advise, they do not block. */
  onPublishAnyway: () => void;
  popoverOpen: boolean;
  onClosePopover: () => void;
  onJump: (pageId: number) => void;
};

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

function PublishPopover({
  failing,
  onClose,
  onJump,
  onPublishAnyway,
  publishing,
}: {
  failing: FailingCheck[];
  onClose: () => void;
  onJump: (id: number) => void;
  onPublishAnyway: () => void;
  publishing: boolean;
}) {
  useEscape(onClose);
  return (
    <View
      accessibilityLabel="Before you can publish"
      accessibilityLiveRegion="polite"
      style={{
        position: 'absolute',
        top: HEADER_HEIGHT - 6,
        right: S[4],
        zIndex: 40,
        width: 380,
        maxWidth: '92%',
        backgroundColor: E.surface,
        borderRadius: 14,
        borderWidth: 1,
        borderColor: E.line,
        paddingVertical: S[2],
        shadowColor: '#000',
        shadowOffset: {width: 0, height: 10},
        shadowOpacity: 0.14,
        shadowRadius: 28,
        elevation: 10,
      }}>
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          paddingLeft: S[4],
          paddingRight: S[1],
        }}>
        <CCText
          accessibilityRole="header"
          style={{flex: 1, fontSize: T.base, fontWeight: '700', color: E.ink}}>
          {`Still open · ${plural(failing.length, 'check')}`}
        </CCText>
        <IconButton
          icon="close"
          label="Close publish checks"
          onPress={onClose}
        />
      </View>
      {failing.map((c, i) => (
        <View
          key={`${c.label}-${i}`}
          style={{
            flexDirection: 'row',
            gap: S[3],
            paddingHorizontal: S[4],
            paddingVertical: S[3],
            borderTopWidth: i ? 1 : 0,
            borderTopColor: E.line,
          }}>
          <Ionicons
            name="alert-circle-outline"
            size={18}
            color={STATUS.warn.fg}
            style={{marginTop: 1}}
          />
          <View style={{flex: 1, gap: S[2]}}>
            <View style={{gap: 2}}>
              <CCText
                style={{
                  fontSize: T.sm + 1,
                  fontWeight: '600',
                  color: E.ink,
                  lineHeight: 20,
                }}>
                {c.label}
              </CCText>
              <CCText style={{fontSize: T.sm, color: E.muted, lineHeight: 19}}>
                {c.chapters.length
                  ? `${
                      c.chapters.length === 1 ? 'Chapter' : 'Chapters'
                    } ${c.chapters.join(', ')}`
                  : c.detail}
              </CCText>
            </View>
            {c.jumpPageId != null ? (
              <Button
                label={`Go to chapter ${c.chapters[0]}`}
                variant="secondary"
                trailingIcon="arrow-forward"
                onPress={() => onJump(c.jumpPageId as number)}
                style={{alignSelf: 'flex-start'}}
              />
            ) : null}
          </View>
        </View>
      ))}
      <View
        style={{
          borderTopWidth: 1,
          borderTopColor: E.line,
          paddingHorizontal: S[4],
          paddingTop: S[3],
          paddingBottom: S[2],
          gap: S[2],
        }}>
        <CCText style={{fontSize: T.sm, color: E.muted, lineHeight: 19}}>
          These are advice, not a lock: you can still publish.
        </CCText>
        <Button
          label="Publish anyway"
          variant="accent"
          disabled={publishing}
          onPress={onPublishAnyway}
          style={{alignSelf: 'flex-start'}}
        />
      </View>
    </View>
  );
}

/**
 * One quiet row: back, the course, how far review has got, the next thing to
 * look at, and Publish. Everything else about the course lives in the chat or
 * in a chapter's ⋯ menu.
 */
export default function EditorHeader(p: Props) {
  const published = p.reviewState === 'published';
  const stateWord = published
    ? 'Published'
    : p.reviewState === 'in_review'
    ? 'In review'
    : 'Draft';
  return (
    <View style={{zIndex: 30}}>
      <View
        style={{
          minHeight: HEADER_HEIGHT,
          flexDirection: 'row',
          alignItems: 'center',
          gap: S[2],
          paddingHorizontal: S[3],
          backgroundColor: E.surface,
          borderBottomWidth: 1,
          borderBottomColor: E.line,
        }}>
        <IconButton
          icon="arrow-back"
          label="Back to courses"
          onPress={p.onBack}
        />
        {p.showRailToggle ? (
          <IconButton
            icon="list"
            label="Show chapters"
            onPress={p.onShowRail}
          />
        ) : null}
        <View style={{flex: 1, minWidth: 0, paddingHorizontal: S[1]}}>
          <View style={{flexDirection: 'row', alignItems: 'center', gap: S[1], minWidth: 0}}>
            <CCText
              accessibilityRole="header"
              numberOfLines={1}
              style={{flexShrink: 1, fontSize: T.lg, fontWeight: '700', color: E.ink}}>
              {p.title}
            </CCText>
            <IconButton icon="pencil" label="Rename course" size={32} onPress={p.onRename} />
          </View>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: S[2],
              marginTop: 1,
            }}>
            <CCText
              style={{
                fontSize: T.xs,
                fontWeight: '600',
                color: published ? STATUS.good.fg : E.muted,
              }}>
              {stateWord}
            </CCText>
            <CCText style={{fontSize: T.xs, color: E.muted}}>·</CCText>
            <CCText
              style={{
                fontSize: T.xs,
                color: E.muted,
                fontVariant: ['tabular-nums'],
              }}>
              {`${p.progress.done}/${p.progress.total} reviewed`}
            </CCText>
          </View>
        </View>
        <Button
          label={
            p.next ? (p.narrow ? 'Next' : 'Next to review') : 'All reviewed'
          }
          accessibilityLabel={
            p.next ? `Next to review: chapter ${p.next.number}` : 'All reviewed'
          }
          variant="quiet"
          trailingIcon={p.next ? 'chevron-forward' : 'checkmark'}
          disabled={!p.next}
          onPress={p.onNext}
        />
        {published ? (
          <>
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: 4,
                paddingHorizontal: S[2],
              }}>
              <Ionicons
                name="checkmark-circle"
                size={16}
                color={STATUS.good.fg}
              />
              <CCText
                style={{
                  fontSize: T.sm,
                  fontWeight: '600',
                  color: STATUS.good.fg,
                }}>
                Live
              </CCText>
            </View>
            <Button
              label="Unpublish"
              variant="secondary"
              onPress={p.onUnpublish}
            />
          </>
        ) : (
          <Button
            label={p.publishing ? 'Publishing…' : 'Publish'}
            accessibilityLabel={
              p.gateOk
                ? 'Publish'
                : `Publish — ${plural(p.failing.length, 'check')} to fix first`
            }
            accessibilityState={
              p.gateOk ? undefined : {expanded: p.popoverOpen}
            }
            variant="primary"
            trailingIcon={p.gateOk ? undefined : 'chevron-down'}
            disabled={p.publishing}
            onPress={p.onPublish}
          />
        )}
      </View>
      {p.popoverOpen && !p.gateOk ? (
        <PublishPopover
          failing={p.failing}
          onClose={p.onClosePopover}
          onJump={p.onJump}
          onPublishAnyway={p.onPublishAnyway}
          publishing={p.publishing}
        />
      ) : null}
    </View>
  );
}
